import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";

export default function EnergyCompareDialog({ open, onClose, API_BASE, currentDeviceId }) {
  const { t, i18n } = useTranslation();
  const [energyType, setEnergyType] = useState("");
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const currentDeviceType = localStorage.getItem("deviceType") || "analyzer";

  const energyOptions = [
    { value: "import-active", label: t('energyReports.importActive'), deviceTypes: ["analyzer", "reactive", "gruparge"] },
    { value: "export-active", label: t('energyReports.exportActive'), deviceTypes: ["analyzer", "reactive", "gruparge"] },
    { value: "import-reactive", label: t('energyReports.importReactive'), deviceTypes: ["analyzer"] },
    { value: "export-reactive", label: t('energyReports.exportReactive'), deviceTypes: ["analyzer"] },
    { value: "inductive", label: t('energyReports.inductiveEnergy'), deviceTypes: ["reactive", "gruparge"] },
    { value: "capacitive", label: t('energyReports.capacitiveEnergy'), deviceTypes: ["reactive", "gruparge"] },
    { value: "apparent", label: t('energyReports.apparentEnergy'), deviceTypes: ["analyzer", "reactive"] },
    { value: "quad1", label: t('energyReports.quad1'), deviceTypes: ["analyzer"] },
    { value: "quad2", label: t('energyReports.quad2'), deviceTypes: ["analyzer"] },
    { value: "quad3", label: t('energyReports.quad3'), deviceTypes: ["analyzer"] },
    { value: "quad4", label: t('energyReports.quad4'), deviceTypes: ["analyzer"] },
  ];

  const filteredEnergyOptions = energyOptions.filter((opt) =>
    opt.deviceTypes.includes(currentDeviceType)
  );

  useEffect(() => {
    async function fetchDevices() {
      if (!energyType) return;
      try {
        const res = await fetch(
          `${API_BASE}/gateways/devices/by-energy-type?energyType=${energyType}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        );
        if (!res.ok) throw new Error(t('energyReports.errorDevices'));

        const json = await res.json();

        // 🧠 şu an ekrandaki cihazı (currentDeviceId) listeden çıkar
        const filteredDevices = Array.isArray(json)
          ? json.filter((d) => d.id?.toString() !== currentDeviceId?.toString())
          : [];

        setDevices(filteredDevices);
      } catch (err) {
        console.error("Device list error:", err);
        setDevices([]);
      }
    }
    fetchDevices();
  }, [energyType, currentDeviceId]);


  useEffect(() => {
    if (currentDeviceId && !selectedDevices.includes(currentDeviceId.toString())) {
      setSelectedDevices((prev) => [...prev, currentDeviceId.toString()]);
    }
  }, [currentDeviceId]);

  const handleDeviceSelect = (e) => {
    const values = Array.from(e.target.selectedOptions, (opt) => opt.value);
    setSelectedDevices(values);
  };

  const handleDownload = async () => {
    if (!energyType || !selectedDevices.length || !start || !end) {
      alert(t('energyReports.fillAll'));
      return;
    }

    try {
      setIsLoading(true);

      const lang = i18n.language.startsWith("tr") ? "tr" : "en";
      const compareNames = encodeURIComponent(selectedDevices.join(","));
      const url = `${API_BASE}/reports/export-energy-comparison?currentId=${currentDeviceId}&compareNames=${compareNames}&energyType=${energyType}&start=${start}&end=${end}&lang=${lang}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(t('reports.creationError'));

      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `comparison_${energyType}_${start}_${end}.png`;
      link.click();
      URL.revokeObjectURL(link.href);
      onClose();
    } catch (err) {
      alert(t('energyReports.errorCreate'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100">
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <Dialog.Title className="text-lg font-semibold text-gray-800 mb-4">
              {t('energies.compareReport')}
            </Dialog.Title>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">{t('energyReports.energyType')}</label>
              <select
                value={energyType}
                onChange={(e) => setEnergyType(e.target.value)}
                disabled={isLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">{t('energyReports.select')}</option>
                {filteredEnergyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {energyType && (
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">{t('common.devices')}</label>
                <select
                  multiple
                  value={selectedDevices}
                  onChange={handleDeviceSelect}
                  disabled={isLoading}
                  className="w-full border rounded-md px-3 py-2 h-32"
                >
                  {devices.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('energyReports.multiSelectHint', 'Ctrl / Cmd ile birden fazla cihaz seçebilirsiniz.')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('energyReports.startDate')}</label>
                <input
                  type="date"
                  value={start}
                  onChange={handleDateChange(setStart)}
                  disabled={isLoading}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('energyReports.endDate')}</label>
                <input
                  type="date"
                  value={end}
                  onChange={handleDateChange(setEnd)}
                  disabled={isLoading}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md text-white ${isLoading
                  ? "bg-emerald-400 cursor-wait"
                  : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    {t('energyReports.preparing')}
                  </span>
                ) : (
                  `${t('common.download')} (PNG)`
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
}
